import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { BaseComponent } from '../../../../core/components/base/base.component';
import { FileObject, mapMediaToFileObject } from '../../../../core/interfaces/file-object';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { PatientRiskItemService } from '../../../../core/services/patient-risk-item.service';
import { PatientService } from '../../../../core/services/patient.service';
import { RISK_STATUSES, riskCategoryLabel, riskLocationLabel, riskResolutionLabel, riskSeverityLabel } from '../../../../shared/components/risk-suggestion-list/risk-suggestion.model';

/**
 * The risk-item DETAIL VIEW.
 *
 * Workstream J made the clinical timeline's risk-item rows display-only because there was nowhere
 * to open them, and left `PatientRiskItemService.setEditingRiskItem` in place as the hand-off
 * channel. This dialog was that somewhere.
 *
 * ── WHAT IT IS REACHED FROM NOW (N5) ─────────────────────────────────────────────────────
 *
 * ONE PATH ONLY: a register row whose `patientId` IS NULL. `setEditingRiskItem` now belongs to
 * `patient-risk-detail` — the routed page workstream N built — and every risk-item row that names
 * a patient opens there instead, from the register and from the patient file alike.
 *
 * A row that names NO patient (§5c — "pharmacy had no ceftriaxone", and roughly the point of the
 * register) has no patient file to open and therefore no route, so it opens here. That is the
 * correct answer for such a row rather than a fallback, and it is why this dialog is still wired
 * up rather than deleted.
 *
 * ── WHAT IS EDITABLE HERE, AND WHAT IS NOT ───────────────────────────────────────────────
 *
 * The server whitelists `category, severity, dateOfIssue, issue, notes, status`. This dialog
 * changes only `status` and `notes`, and shows everything else read-only, because two of the
 * read-only fields are load-bearing:
 *
 *  - THE QUOTE IS EVIDENCE (D8, §1: "no editing of an item's AI-extracted quote — it is verbatim
 *    or it is gone"). Before a doctor puts "no crash cart on the ward" on their own letterhead
 *    they must be able to see the sentence they actually said. The server will not accept an edit
 *    to it and neither does this dialog offer one.
 *  - THE FACILITY IS A SNAPSHOT (§4, D5, D6). `hospitalId`, `hospitalName`, `wardName` and
 *    `location` were resolved server-side once, at the moment the doctor confirmed the item, and
 *    are deliberately never re-resolved: the patient gets discharged and re-admitted elsewhere,
 *    and a live join would silently re-file March's issue under the wrong hospital. The server
 *    ignores them on update; showing them as editable would be a lie about what the button does.
 */
@Component({
  selector: 'app-risk-item-detail-dialog',
  templateUrl: './risk-item-detail-dialog.component.html',
  styleUrls: ['./risk-item-detail-dialog.component.scss']
})
export class RiskItemDetailDialogComponent extends BaseComponent {
  item: any;
  notes: string = '';
  saving = false;

  /** D9, BY IMPORT — see `risk-suggestion.model.ts`. Never restate the four values. */
  readonly statusOptions: readonly string[] = RISK_STATUSES;
  /**
   * Hides Facility, Patient, Notes and the provenance footer (user request, 2026-08-31).
   * Set to `true` to bring all four back — the markup is intact behind a single `*ngIf`.
   *
   * A field rather than a deletion because the request was to hide them "for now". Hidden data
   * is still stored, still filtered on, and still exported; only this view stops showing it.
   */
  readonly showExtendedDetail = false;


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { item: any },
    private dialogRef: MatDialogRef<RiskItemDetailDialogComponent>,
    private patientRiskItemService: PatientRiskItemService,
    private patientService: PatientService,
    private router: Router,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
    this.item = data?.item || {};
    this.notes = this.item.notes || '';
    this.loadMedia(this.item);
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════
  //  §28 / P5-P6 — PHOTOGRAPHS
  // ═══════════════════════════════════════════════════════════════════════════════════════
  //
  // Same two-array shape as `patient-risk-detail` and `patient-note-detail`, for the same reason:
  // `app-file-upload` re-emits its WHOLE accumulated array on every `fileUpload` in multiple mode,
  // so the bound input and the working set have to be different objects.
  //
  // What differs here is WHEN it saves. This dialog has no form submit — `setStatus` and
  // `saveNotes` each send one narrow field the moment the doctor acts — so the gallery gets its
  // own explicit "Save photographs" button rather than riding on someone else's save.

  /** Bound to `[fileObjects]` — the gallery's starting contents. */
  loadedMediaObjects: FileObject[] = [];

  /** The working set: saved photographs plus anything added or removed this session. */
  mediaObjects: FileObject[] = [];

  private loadMedia(item: any) {
    const media = Array.isArray(item?.media) ? item.media : [];
    this.loadedMediaObjects = media.length ? mapMediaToFileObject(media) : [];
    this.mediaObjects = [...this.loadedMediaObjects];
  }

  /**
   * True when the gallery differs from what is stored — a photograph added, or one removed.
   *
   * Compared by count AND by the set of persisted ids, so removing one saved photo and adding one
   * new one in the same sitting still registers as a change. Without the id check that pair of
   * edits would cancel out to an equal length and the save button would stay disabled.
   */
  get hasMediaChange(): boolean {
    if (this.mediaObjects.length !== this.loadedMediaObjects.length) return true;
    const storedIds = this.loadedMediaObjects.map(file => file.id).filter(id => !!id).sort();
    const workingIds = this.mediaObjects.map(file => file.id).filter(id => !!id).sort();
    return storedIds.join(',') !== workingIds.join(',');
  }

  /** De-duplicated by `url` — multiple mode re-emits the whole array on every add. */
  onRiskMediaUpload(files: FileObject[]) {
    const incoming = (files || []).filter(
      file => !this.mediaObjects.some(existing => existing.url === file.url)
    );
    this.mediaObjects = [...this.mediaObjects, ...incoming];
  }

  onRiskMediaEdited(files: FileObject[]) {
    this.mediaObjects = files || [];
  }

  /** Dropped locally; a persisted row is soft-deleted server-side on the next save. */
  onRiskMediaDelete(fileObject: FileObject) {
    this.mediaObjects = this.mediaObjects.filter(file => file.url !== fileObject.url);
  }

  /**
   * How many stored photographs were attached AFTER this item was reported.
   *
   * ── ATTACHING TO A `Reported` ITEM IS ALLOWED. DO NOT ADD A GATE. ─────────────────────
   *
   * USER DECISION, 2026-09-01: *"yes leave it up to the user, same as the facility edit."*
   *
   * The PDF has already gone to the hospital, so a photograph attached now is NOT in the document
   * they hold — the stored evidence and the sent document diverge. That is accepted deliberately,
   * and it is the same call §18 already made for the facility snapshot, where a `reportedAt` gate
   * was proposed and explicitly declined. The reasoning is the doctor's judgement, not an
   * oversight: an issue gets reported and then more evidence surfaces, and a register that REFUSES
   * the evidence is worse than one whose export is a point-in-time snapshot. There is no block, no
   * confirmation and no disabled state anywhere on this path, on purpose.
   *
   * What there IS instead is this count, which costs nothing: the divergence is stated quietly
   * next to the gallery, where the doctor is already looking at both the photographs and the
   * reported date, so nobody discovers it by being asked about a photo the hospital never saw.
   */
  get photosAddedAfterReporting(): number {
    if (!this.item?.reportedAt) return 0;
    const reportedAt = new Date(this.item.reportedAt).getTime();
    const media = Array.isArray(this.item.media) ? this.item.media : [];
    return media.filter((row: any) => row?.createdAt && new Date(row.createdAt).getTime() > reportedAt).length;
  }

  /**
   * Sends the gallery as `media`, which the service turns into multipart.
   *
   * Goes through the SAME `save()` as status and notes, so the response — which carries the full
   * re-read `media` array — replaces `this.item` and re-seeds the gallery. That re-seed is what
   * turns the just-uploaded blob URLs into persisted rows with ids; without it a second save would
   * upload the same photographs again.
   */
  saveMedia() {
    if (!this.hasMediaChange || this.saving) return;
    this.save({ media: this.mediaObjects });
  }

  categoryLabel(category?: string): string {
    return riskCategoryLabel(category);
  }

  severityLabel(severity?: string): string {
    return riskSeverityLabel(severity);
  }

  locationLabel(location?: string | null): string {
    return riskLocationLabel(location);
  }

  /**
   * How §4 landed on this facility, in words. `'rooms'` reads "In-rooms / OPD — no hospital
   * admission covers this date", which D6 exists to make a first-class outcome rather than a
   * failure to resolve.
   */
  resolutionLabel(): string {
    return riskResolutionLabel(this.item?.resolvedBy, this.item?.location);
  }

  /** The facility heading, preferring the D5 snapshot over the joined row. */
  facilityLabel(): string {
    const snapshot = String(this.item?.hospitalName ?? '').trim();
    if (snapshot) return snapshot;
    const joined = String(this.item?.hospital?.name ?? '').trim();
    if (joined) return joined;
    return riskLocationLabel(this.item?.location);
  }

  /**
   * ⚠ LEGACY-SHAPE GUARD, KEPT DELIBERATELY. `patientId` was nullable and unconstrained until
   * 2026-09-01, when the user's decision made the workflows and the UI guarantee a patient on
   * every new item — with no migration and the column still nullable, so the rows written before
   * it are still here and this dialog is the only surface that opens them
   * (`risk-management-list.openRiskItem` routes them here precisely because they have no patient
   * file). Do not remove this because "patientId is never null now": it is never null on anything
   * NEW, which is a statement about creation and not about the table.
   */
  /**
   * Two DIFFERENT facts, which this used to collapse into one sentence.
   *
   *   patientId null           -> genuinely not about a patient. LEGACY SHAPE: the column is
   *                               still nullable, but every creation path now supplies a patient
   *                               (user decision, 2026-09-01), so new rows never look like this.
   *   patientId set, Patient   -> the association was not loaded on this payload. NOT the same
   *   association absent          claim, and saying "no patient" about it is simply wrong.
   *
   * This is not hypothetical. `markItemsReported` used to emit rows without their includes, and
   * because the client REPLACES a row on `riskItemUpdated` rather than merging, every exported
   * item rendered as "No patient" — a real bug reported as data loss. The include is fixed, but
   * the label must not be able to make that claim again on a thin payload.
   */
  patientLabel(): string {
    const patient = this.item?.Patient;
    const noPatient = 'No patient — a practice-level facility issue';
    const notLoaded = 'Patient not loaded on this record';
    if (!patient) {
      return this.item?.patientId ? notLoaded : noPatient;
    }
    const surname = patient.surname || '';
    const name = patient.name || '';
    const label = `${surname}${surname && name ? ', ' : ''}${name}`.trim();
    return label || (this.item?.patientId ? notLoaded : noPatient);
  }

  get hasNotesChange(): boolean {
    return (this.notes || '') !== (this.item?.notes || '');
  }

  async setStatus(status: string) {
    if (!this.item?.id || this.item.status === status) return;

    if (status === 'Resolved' || status === 'Dismissed') {
      const confirmed = await this.showConfirmationDialog(
        'Confirm Risk Item Status',
        `Are you sure you want to mark this issue as ${status}?`,
        this.item?.issue ? [{ patient: this.item.issue }] : undefined,
        'No',
        'Yes'
      );
      if (!confirmed) return;
    }

    this.save({ status });
  }

  saveNotes() {
    if (!this.hasNotesChange) return;
    this.save({ notes: this.notes });
  }

  /**
   * Sends ONLY the changed field. The server ignores anything outside its whitelist, so a full
   * round-trip would be safe — but sending the whole object here would make it look as though the
   * facility snapshot and the quote were being rewritten every time, which is the confusion D1-1's
   * whitelist exists to prevent.
   */
  private save(changes: any) {
    if (this.saving) return;
    this.saving = true;

    this.patientRiskItemService.updateRiskItem(this.item.id, changes)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          this.saving = false;
          if (result?.data) {
            this.item = result.data;
            this.notes = this.item.notes || '';
            // §28 — RE-SEED THE GALLERY from the response. The server sends the full re-read
            // `media` array on every update, so this turns the blob URLs of anything just
            // uploaded into persisted rows carrying ids. Without it the objects would still look
            // "new" and the next save would upload the same photographs a second time.
            this.loadMedia(this.item);
          }
          this.showToastMessage('Risk item updated');
        },
        error: (error) => {
          this.saving = false;
          console.error('Error updating risk item: ', error);
          this.showToastMessage('There was an error updating the risk item. Please try again.');
        }
      });
  }

  openPatientFile() {
    const patientId = this.item?.patientId;
    if (!patientId) return;

    this.patientService.getPatientById(patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.dialogRef.close();
          this.router.navigate(['/patients', {
            outlets: {
              patient: ['file', patientId],
            }
          }]);
        },
        error: (error) => {
          console.error(error);
          this.showToastMessage('There was an error loading the patient file. Please try again.');
        }
      });
  }

  close() {
    this.dialogRef.close();
  }
}
